import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router';
import { Splash } from './components/ui.tsx';
import { homePath, useMe } from './lib/auth.ts';
import { LoginPage } from './pages/auth/LoginPage.tsx';
import { DEMO } from './lib/demo.ts';
import { DemoStartPage } from './pages/demo/Demo.tsx';
import { VerifyPage } from './pages/auth/VerifyPage.tsx';
import { ConfirmEmailPage } from './pages/auth/ConfirmEmailPage.tsx';
import { LandlordShell } from './pages/landlord/LandlordShell.tsx';
import { OverviewPage } from './pages/landlord/OverviewPage.tsx';
import { PropertiesPage } from './pages/landlord/PropertiesPage.tsx';
import { NewPropertyPage } from './pages/landlord/NewPropertyPage.tsx';
import { PropertyLayout } from './pages/landlord/PropertyLayout.tsx';
import { NewLeasePage } from './pages/landlord/NewLeasePage.tsx';
import { LeaseLogPage, PropertyLogPage } from './pages/landlord/PropertyLog.tsx';
import { LandlordFinanceTab } from './pages/landlord/Finance.tsx';
import { FinancePage } from './pages/landlord/FinancePage.tsx';
import { TaxesPage } from './pages/landlord/TaxesPage.tsx';
import { LandlordHistoryPage } from './pages/landlord/HistoryPage.tsx';
import { PropertySettingsTab } from './pages/landlord/PropertySettings.tsx';
import { TenantInvoicesPage } from './pages/tenant/InvoicesPage.tsx';
import {
  LandlordFilesTab,
  LandlordInspectionTab,
  LandlordMaintenanceTab,
  LandlordTenancyTab,
  PropertyOverviewTab,
  SoonTab,
} from './pages/landlord/PropertyTabs.tsx';
import { TenantShell } from './pages/tenant/TenantShell.tsx';
import {
  TenantContractTab,
  TenantDetailsTab,
  TenantFilesTab,
  TenantInspectionTab,
  TenantMaintenanceTab,
  TenantPropertyPage,
} from './pages/tenant/TenantPropertyPage.tsx';
import { TenantSettingsPage } from './pages/tenant/TenantSettingsPage.tsx';
import { HistoryPage } from './pages/tenant/HistoryPage.tsx';
import { AccountPage } from './pages/shared/AccountPage.tsx';
import {
  LandlordMessagesPage,
  LandlordMessagesTab,
  TenantMessagesPage,
} from './pages/shared/MessagesPages.tsx';
import { AdminOverview, AdminSectionPage, AdminShell } from './pages/admin/AdminShell.tsx';

/** Kirjautumaton käyttäjä ohjataan kirjautumissivulle. */
function RequireAuth() {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <Splash />;
  if (!me) return <Navigate to="/kirjaudu" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

function HomeRedirect() {
  const { data: me } = useMe();
  return me ? <Navigate to={homePath(me)} replace /> : null;
}

/*
 * Polut ovat suomeksi. Vuokranantaja, vuokralainen ja ylläpito ovat omia osioitaan;
 * sama käyttäjä voi vaihtaa vuokranantajan ja vuokralaisen näkymän välillä käyttäjävalikosta.
 */
export const router = createBrowserRouter([
  { path: '/kirjaudu', element: DEMO ? <DemoStartPage /> : <LoginPage /> },
  { path: '/kirjaudu/vahvista', element: <VerifyPage /> },
  { path: '/vahvista-sahkoposti', element: <ConfirmEmailPage /> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        path: 'vuokranantaja',
        element: <LandlordShell />,
        children: [
          { index: true, element: <OverviewPage /> },
          { path: 'asunnot', element: <PropertiesPage /> },
          { path: 'asunnot/uusi', element: <NewPropertyPage /> },
          {
            path: 'asunnot/:id',
            element: <PropertyLayout />,
            children: [
              { index: true, element: <PropertyOverviewTab /> },
              { path: 'vuokrasuhde', element: <LandlordTenancyTab /> },
              { path: 'vuokrasuhde/uusi', element: <NewLeasePage /> },
              { path: 'tarkastus', element: <LandlordInspectionTab /> },
              { path: 'talous', element: <LandlordFinanceTab /> },
              { path: 'huolto', element: <LandlordMaintenanceTab /> },
              { path: 'tiedostot', element: <LandlordFilesTab /> },
              { path: 'viestit', element: <LandlordMessagesTab /> },
              { path: 'viestit/:threadId', element: <LandlordMessagesTab /> },
              { path: 'asetukset', element: <PropertySettingsTab /> },
            ],
          },
          { path: 'talous', element: <FinancePage /> },
          { path: 'viestit', element: <LandlordMessagesPage /> },
          { path: 'viestit/:threadId', element: <LandlordMessagesPage /> },
          { path: 'verot', element: <TaxesPage /> },
          { path: 'historia', element: <LandlordHistoryPage /> },
          { path: 'tili', element: <AccountPage /> },
          { path: 'loki/asunto/:id', element: <PropertyLogPage /> },
          { path: 'loki/vuokrasuhde/:id', element: <LeaseLogPage /> },
        ],
      },
      {
        path: 'vuokralainen',
        element: <TenantShell />,
        children: [
          { index: true, element: <Navigate to="asunto" replace /> },
          {
            path: 'asunto',
            element: <TenantPropertyPage />,
            children: [
              { index: true, element: <TenantDetailsTab /> },
              { path: 'sopimus', element: <TenantContractTab /> },
              { path: 'tiedostot', element: <TenantFilesTab /> },
              { path: 'tarkastus', element: <TenantInspectionTab /> },
              { path: 'huolto', element: <TenantMaintenanceTab /> },
            ],
          },
          { path: 'laskut', element: <TenantInvoicesPage /> },
          { path: 'viestit', element: <TenantMessagesPage /> },
          { path: 'viestit/:threadId', element: <TenantMessagesPage /> },
          { path: 'historia', element: <HistoryPage /> },
          { path: 'tiedot', element: <TenantSettingsPage /> },
          { path: 'loki/:id', element: <LeaseLogPage /> },
          { path: 'tili', element: <AccountPage /> },
        ],
      },
      {
        path: 'admin',
        element: <AdminShell />,
        children: [
          { index: true, element: <AdminOverview /> },
          { path: ':section', element: <AdminSectionPage /> },
          { path: ':section/:sub', element: <AdminSectionPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
